import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { sanitizeSearchInput, sanitizeString } from '../utils/security';
import {
  fetchCustomers,
  searchCustomers,
  createCustomer,
  toggleCustomerStatus,
} from '../services/customersService';
import {
  fetchCities,
  fetchDistrictsByCity,
} from '../services/locationsService';
import { useConfirmDialog } from '../contexts/ConfirmContext';
import TableListFooter from '../components/TableListFooter';
import { ROWS_PER_PAGE_OPTIONS } from '../constants/pagination';

const rowsPerPageOptions = [10, 25, 50, 100];

const safeText = (value) => sanitizeString(value ?? '');

const getErrorMessage = (error, fallback) => {
  if (!error) return fallback;

  if (error?.status === 404) return 'API endpoint bulunamadı.';
  if (error?.status === 401) return 'Kimlik doğrulama hatası. Lütfen tekrar giriş yapın.';
  if (error?.status === 403) return 'Bu işlem için yetkiniz yok.';

  let message =
    error?.message ||
    error?.data?.mesaj ||
    error?.data?.message ||
    error?.data?.error ||
    error?.data?.hata ||
    fallback;

  if (message && typeof message === 'string') {
    message = message
      .replace(/&#x27;/g, "'")
      .replace(/&#39;/g, "'")
      .replace(/&quot;/g, '"')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>');
  }

  if (message === 'Failed to fetch') {
    return 'Sunucuya bağlanılamadı. İnternet bağlantınızı kontrol edin.';
  }

  return message || fallback;
};

const CustomerManagementPage = () => {
  const { confirm } = useConfirmDialog();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [filters, setFilters] = useState({ search: '', status: 'active' });
  const [activeStatusTab, setActiveStatusTab] = useState('active'); // 'active' veya 'passive'
  const [searchInput, setSearchInput] = useState(''); // Arama input'u için ayrı state
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  const [paginationMeta, setPaginationMeta] = useState({ total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const addCustomerFormRef = useRef(null);
  const [formData, setFormData] = useState({ adi: '', soyadi: '', telefon: '', adres: '', cityId: '', districtId: '', isTest: false });
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // İl ve ilçe state'leri
  const [iller, setIller] = useState([]);
  const [ilceler, setIlceler] = useState([]);
  const [illerLoading, setIllerLoading] = useState(false);
  const [ilcelerLoading, setIlcelerLoading] = useState(false);

  const sanitizedSearchValue = useMemo(() => {
    const sanitized = sanitizeSearchInput(filters.search);
    return sanitized && sanitized.trim().length > 0 ? sanitized : undefined;
  }, [filters.search]);

  const filterParams = useMemo(
    () => ({
      aktif: activeStatusTab === 'active',
      search: sanitizedSearchValue,
    }),
    [activeStatusTab, sanitizedSearchValue]
  );

  const loadCustomers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // Eğer arama terimi varsa ve 3+ karakter ise, arama endpoint'ini kullan
      if (filterParams.search && filterParams.search.trim().length >= 3) {
        console.log('[CustomerManagementPage] Using search endpoint with term:', filterParams.search);
        const response = await searchCustomers(filterParams.search, {
          aktif: activeStatusTab === 'active',
          sayfa: currentPage,
          limit: rowsPerPage,
        });
        const list = response?.veri?.musteriler || response?.veri || [];
        
        // Artık frontend'de filtreleme yapmıyoruz, backend'den zaten filtrelenmiş veriler geliyor
        
        // Pagination bilgilerini al - hem sayfalama objesi hem de direkt veri içindeki alanları kontrol et
        const pagination = response?.veri?.sayfalama || {};
        const veri = response?.veri || {};
        
        setCustomers(list);
        setPaginationMeta({
          total: pagination.toplamMusteri || veri?.toplam || list.length,
          totalPages: pagination.toplamSayfa || veri?.toplamSayfa || Math.ceil((pagination.toplamMusteri || veri?.toplam || list.length) / rowsPerPage) || 1,
        });
        setCurrentPage(pagination.mevcutSayfa || veri?.sayfa || currentPage);
      } else {
        // Normal liste endpoint'ini kullan
        const requestParams = {
          page: currentPage,
          limit: rowsPerPage,
          aktif: activeStatusTab === 'active',
        };
        console.log('[CustomerManagementPage] Loading customers with params:', requestParams);
        const response = await fetchCustomers(requestParams);
        const list = response?.veri?.musteriler || response?.veri || [];
        
        // Pagination bilgilerini al - hem sayfalama objesi hem de direkt veri içindeki alanları kontrol et
        const pagination = response?.veri?.sayfalama || {};
        const veri = response?.veri || {};
        
        setCustomers(list);
        setPaginationMeta({
          total: pagination.toplamMusteri || veri?.toplam || list.length,
          totalPages: pagination.toplamSayfa || veri?.toplamSayfa || Math.ceil((pagination.toplamMusteri || veri?.toplam || list.length) / rowsPerPage) || 1,
        });
        setCurrentPage(pagination.mevcutSayfa || veri?.sayfa || currentPage);
      }
    } catch (err) {
      setError(getErrorMessage(err, 'Müşteriler yüklenemedi'));
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  }, [currentPage, rowsPerPage, filterParams, activeStatusTab]);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  // İlleri yükle
  useEffect(() => {
    const loadCities = async () => {
      setIllerLoading(true);
      try {
        const response = await fetchCities();
        const list = response?.veri?.iller || response?.veri?.cities || response?.veri || [];
        setIller(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error('İller yüklenemedi:', err);
        setIller([]);
      } finally {
        setIllerLoading(false);
      }
    };
    
    loadCities();
  }, []);

  // İlçeleri yükle
  useEffect(() => {
    const loadDistricts = async () => {
      if (!formData.cityId || formData.cityId.trim() === '') {
        console.log('[CustomerManagementPage] No cityId, clearing districts');
        setIlceler([]);
        return;
      }
      
      const cityIdNum = parseInt(formData.cityId, 10);
      if (isNaN(cityIdNum)) {
        console.warn('[CustomerManagementPage] Invalid cityId:', formData.cityId);
        setIlceler([]);
        return;
      }
      
      console.log('[CustomerManagementPage] Loading districts for cityId:', cityIdNum);
      setIlcelerLoading(true);
      try {
        const response = await fetchDistrictsByCity(cityIdNum);
        console.log('[CustomerManagementPage] fetchDistrictsByCity response:', response);
        console.log('[CustomerManagementPage] response.veri:', response?.veri);
        console.log('[CustomerManagementPage] response.veri?.ilceler:', response?.veri?.ilceler);
        console.log('[CustomerManagementPage] response.veri?.districts:', response?.veri?.districts);
        
        // Farklı response formatlarını kontrol et
        let list = response?.veri?.ilceler || response?.veri?.districts || response?.veri?.ilceList || response?.veri || [];
        
        // Eğer list bir array değilse, kontrol et
        if (!Array.isArray(list)) {
          console.warn('[CustomerManagementPage] Districts list is not an array:', list);
          list = [];
        }
        
        // API formatı: { id: number, adi: string, cityId: number }
        // Direkt kullan, normalize etmeye gerek yok
        const serializedList = list.map((ilce) => {
          // API'den direkt { id, adi, cityId } formatında geliyor
          return {
            id: ilce.id || ilce._id,
            adi: ilce.adi || ilce.ad || ilce.name,
            ad: ilce.adi || ilce.ad || ilce.name, // Geriye dönük uyumluluk için
            name: ilce.name || ilce.adi || ilce.ad, // Geriye dönük uyumluluk için
            cityId: ilce.cityId
          };
        });
        
        console.log('[CustomerManagementPage] Districts list (final, serialized):', serializedList);
        console.log('[CustomerManagementPage] Districts list length:', serializedList.length);
        
        setIlceler(serializedList);
      } catch (err) {
        console.error('[CustomerManagementPage] İlçeler yüklenemedi:', err);
        setIlceler([]);
      } finally {
        setIlcelerLoading(false);
      }
    };
    
    loadDistricts();
  }, [formData.cityId]);

  const handleAddCustomer = async (e) => {
    e.preventDefault();
    setFormError('');
    
    // Validasyon
    if (!formData.adi.trim()) {
      setFormError('Ad zorunludur.');
      return;
    }
    if (!formData.soyadi.trim()) {
      setFormError('Soyad zorunludur.');
      return;
    }
    if (!formData.telefon.trim()) {
      setFormError('Telefon zorunludur.');
      return;
    }
    if (!formData.adres.trim()) {
      setFormError('Adres zorunludur.');
      return;
    }
    if (!formData.cityId || formData.cityId.trim() === '') {
      setFormError('İl seçimi zorunludur.');
      return;
    }
    if (!formData.districtId || formData.districtId.trim() === '') {
      setFormError('İlçe seçimi zorunludur.');
      return;
    }

    setSubmitting(true);
    try {
      // Önce müşteriyi oluştur (sadece temel bilgiler)
      const customerPayload = {
        adi: formData.adi,
        soyadi: formData.soyadi,
        telefon: formData.telefon,
        mail: formData.mail,
        isTest: formData.isTest === true,
      };
      
      const response = await createCustomer(customerPayload);

      if (response?.basarili === false) {
        throw new Error(response?.mesaj || response?.message || 'Müşteri oluşturulamadı');
      }

      const created = response?.veri?.musteri || response?.veri || {};
      const newId = created?.id || created?._id || created?.customerId;
      
      if (newId) {
        // Müşteri oluşturulduktan sonra adres oluştur (eğer adres bilgileri varsa)
        if (formData.adres && formData.cityId && formData.districtId) {
          try {
            const { createAddress } = await import('../services/addressesService');
            await createAddress(newId, {
              adres: formData.adres.trim(),
              cityId: formData.cityId,
              districtId: formData.districtId,
              varsayilan: true,
              aktif: true,
            });
          } catch (addressError) {
            console.error('Adres oluşturma hatası:', addressError);
            // Adres oluşturulamadı ama müşteri oluşturuldu, devam et
          }
        }
        
        navigate(`/customers/${newId}/edit`, { replace: true });
      } else {
        navigate('/customers', { replace: true });
      }

      setIsAddModalOpen(false);
      setFormData({ adi: '', soyadi: '', telefon: '', mail: '', adres: '', cityId: '', districtId: '', isTest: false });
      setIlceler([]);

      if (window.showNotification) {
        window.showNotification('success', 'Müşteri oluşturuldu; formdan devam edebilirsiniz.');
      }
    } catch (error) {
      console.error('Create customer error:', error);
      setFormError(getErrorMessage(error, 'Müşteri oluşturulamadı'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async (customer) => {
    const customerId = customer.id || customer._id;
    const customerName = `${customer.adi || ''} ${customer.soyadi || ''}`.trim() || 'Müşteri';
    const isActive = customer.aktif !== false;

    if (isActive) {
      const confirmed = await confirm({
        title: 'Müşteriyi pasif yap',
        message: `${customerName} adlı müşteri pasif yapılacak. Onaylıyor musunuz?`,
        variant: 'warning',
        confirmLabel: 'Evet',
      });
      if (!confirmed) return;

      try {
        const response = await toggleCustomerStatus(customerId, false);

        if (response?.basarili === false) {
          throw new Error(response?.mesaj || response?.message || 'Müşteri pasif yapılamadı');
        }

        if (window.showNotification) {
          window.showNotification('success', 'Müşteri pasif yapıldı');
        }
        loadCustomers();
      } catch (err) {
        if (window.showNotification) {
          window.showNotification('error', getErrorMessage(err, 'Müşteri pasif yapılamadı'));
        }
      }
    } else {
      const confirmed = await confirm({
        title: 'Müşteriyi aktif yap',
        message: `${customerName} adlı müşteri aktif yapılacak. Onaylıyor musunuz?`,
        variant: 'info',
        confirmLabel: 'Evet',
      });
      if (!confirmed) return;

      try {
        const response = await toggleCustomerStatus(customerId, true);

        if (response?.basarili === false) {
          throw new Error(response?.mesaj || response?.message || 'Müşteri aktif yapılamadı');
        }

        if (window.showNotification) {
          window.showNotification('success', 'Müşteri aktif yapıldı');
        }
        loadCustomers();
      } catch (err) {
        if (window.showNotification) {
          window.showNotification('error', getErrorMessage(err, 'Müşteri aktif yapılamadı'));
        }
      }
    }
  };

  const handleSearchChange = (event) => {
    const value = sanitizeSearchInput(event.target.value, { trimEdges: false });
    setSearchInput(value);
  };

  // Debounce için search input - en az 3 karakter gerekiyor
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      // En az 3 karakter girilmişse arama yap
      if (searchInput.trim().length >= 3) {
        console.log('[CustomerManagementPage] Search input changed (>=3 chars):', searchInput);
        setSearchLoading(true);
        setFilters((prev) => {
          const newFilters = { ...prev, search: searchInput };
          console.log('[CustomerManagementPage] Filters updated:', newFilters);
          return newFilters;
        });
        setCurrentPage(1);
      } else if (searchInput.trim().length === 0) {
        // Arama terimi temizlendiyse, filtreyi de temizle
        setSearchLoading(false);
        setFilters((prev) => {
          const newFilters = { ...prev, search: '' };
          return newFilters;
        });
        setCurrentPage(1);
      } else {
        setSearchLoading(false);
      }
    }, 1000); // 1000ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  const handleRowsPerPageChange = (event) => {
    const value = parseInt(event.target.value, 10);
    if (rowsPerPageOptions.includes(value)) {
      setRowsPerPage(value);
      setCurrentPage(1);
    }
  };

  const handlePageChange = (direction) => {
    setCurrentPage((prev) => {
      const next = prev + direction;
      if (next < 1 || next > paginationMeta.totalPages) return prev;
      return next;
    });
  };

  const renderCustomersTable = () => {
    if (loading) {
      return (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <div className="loading-text">Müşteriler yükleniyor</div>
          <div className="loading-dots">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      );
    }
    if (error) return <div className="error-state">{safeText(error)}</div>;
    if (customers.length === 0) {
      return (
        <div className="empty-state" style={{ padding: '40px', textAlign: 'center' }}>
          <i className="fas fa-search" style={{ fontSize: '32px', color: 'var(--text-light)', marginBottom: '12px' }}></i>
          <div style={{ color: 'var(--text-gray)', fontSize: '14px' }}>Kayıt bulunamadı</div>
        </div>
      );
    }

    return (
      <table className="admins-table">
        <thead>
          <tr>
            <th>Ad Soyad</th>
            <th>Telefon</th>
            <th className="islemler-sutunu">İşlemler</th>
          </tr>
        </thead>
        <tbody>
          {customers.map((customer) => (
            <tr key={customer.id || customer._id}>
              <td>
                <span
                  className="tech-service-name-link"
                  onClick={() =>
                    navigate(`/customers/${customer.id || customer._id}/edit`)
                  }
                >
                  {`${customer.adi || ''} ${customer.soyadi || ''}`.trim() || '—'}
                  {customer.isTest === true && (
                    <span
                      style={{
                        marginLeft: 8,
                        fontSize: 11,
                        fontWeight: 600,
                        color: '#92400e',
                        background: '#fef3c7',
                        padding: '2px 6px',
                        borderRadius: 4,
                      }}
                    >
                      Test
                    </span>
                  )}
                </span>
              </td>
              <td>{customer.telefon || '—'}</td>
              <td className="table-actions islemler-sutunu">
                {customer.aktif !== false ? (
                  <button
                    type="button"
                    className="category-action-btn delete-btn"
                    aria-label="Müşteriyi pasif yap"
                    onClick={() => handleToggleStatus(customer)}
                    title="Pasif yap"
                  >
                    <i className="fas fa-trash"></i>
                  </button>
                ) : (
                  <button
                    type="button"
                    className="category-action-btn move-btn"
                    aria-label="Müşteriyi aktif yap"
                    onClick={() => handleToggleStatus(customer)}
                    title="Aktif yap"
                  >
                    <i className="fas fa-check"></i>
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const totalRecords = paginationMeta.total || customers.length;

  return (
    <main className="main-content customer-management">
      <div className="content-header">
        <h1>Müşteri Yönetimi</h1>
      </div>

      <div className="status-tabs">
        <button
          type="button"
          className={`status-tab-btn ${activeStatusTab === 'active' ? 'active' : ''}`}
          onClick={() => {
            setActiveStatusTab('active');
            setCurrentPage(1);
          }}
        >
          Aktif Müşteriler
        </button>
        <button
          type="button"
          className={`status-tab-btn ${activeStatusTab === 'passive' ? 'active' : ''}`}
          onClick={() => {
            setActiveStatusTab('passive');
            setCurrentPage(1);
          }}
        >
          Pasif Müşteriler
        </button>
      </div>

      <div className="toolbar">
        <div className="search-filter-bar">
          <div className="search-box">
            <i className="fas fa-search"></i>
            <input
              type="text"
              placeholder="İsim, soyad, telefon, mail, adres ile ara (min 3 karakter)..."
              value={searchInput}
              onChange={handleSearchChange}
              maxLength={100}
              autoComplete="off"
            />
            {searchLoading && (
              <i className="fas fa-spinner fa-spin" style={{ right: '10px' }} aria-hidden />
            )}
          </div>
        </div>

        <button type="button" className="primary-btn add-user-btn" onClick={() => setIsAddModalOpen(true)}>
          <i className="fas fa-plus"></i>
          <span>Yeni Müşteri Ekle</span>
        </button>
      </div>

      <div className="table-container">{renderCustomersTable()}</div>

      <TableListFooter
        totalRecords={totalRecords}
        currentPage={currentPage}
        totalPages={paginationMeta.totalPages || 1}
        onPageChange={setCurrentPage}
        loading={loading}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={rowsPerPageOptions}
        onRowsPerPageChange={(value) => handleRowsPerPageChange({ target: { value: String(value) } })}
      />

      {isAddModalOpen && (
        <div className="modal-overlay" role="dialog" aria-modal="true" onClick={() => setIsAddModalOpen(false)}>
          <div 
            className="modal quick-modal" 
            onClick={(e) => e.stopPropagation()}
            style={{
              display: 'flex',
              flexDirection: 'column',
              maxHeight: '90vh',
              overflow: 'hidden'
            }}
          >
            <div 
              className="modal-header"
              style={{
                flexShrink: 0,
                borderBottom: '1px solid #e2e8f0'
              }}
            >
              <h2>Yeni Müşteri Ekle</h2>
              <button type="button" className="close-btn" onClick={() => setIsAddModalOpen(false)} aria-label="Kapat">
                <i className="fas fa-times"></i>
              </button>
            </div>
            <form 
              ref={addCustomerFormRef}
              className="modal-body" 
              onSubmit={handleAddCustomer}
              style={{
                flex: 1,
                overflowY: 'auto',
                overflowX: 'hidden',
                padding: '24px'
              }}
            >
              <div className="form-grid">
                <label className="form-field">
                  <span>Ad *</span>
                  <input 
                    type="text" 
                    name="adi" 
                    value={formData.adi} 
                    onChange={(e) => setFormData((prev) => ({ ...prev, adi: e.target.value }))} 
                    maxLength={100} 
                    required 
                  />
                </label>
                <label className="form-field">
                  <span>Soyad *</span>
                  <input 
                    type="text" 
                    name="soyadi" 
                    value={formData.soyadi} 
                    onChange={(e) => setFormData((prev) => ({ ...prev, soyadi: e.target.value }))} 
                    maxLength={100} 
                    required 
                  />
                </label>
              </div>
              <div className="form-grid">
                <label className="form-field">
                  <span>Telefon *</span>
                  <input 
                    type="text" 
                    name="telefon" 
                    value={formData.telefon} 
                    onChange={(e) => setFormData((prev) => ({ ...prev, telefon: e.target.value }))} 
                    maxLength={50} 
                    required 
                  />
                </label>
                <label className="form-field">
                  <span>E-posta</span>
                  <input 
                    type="email" 
                    name="mail" 
                    value={formData.mail || ''} 
                    onChange={(e) => setFormData((prev) => ({ ...prev, mail: e.target.value }))} 
                    maxLength={100} 
                  />
                </label>
              </div>
              <div className="form-grid">
                <label className="form-field">
                  <span>İl *</span>
                  <select
                    name="cityId"
                    value={formData.cityId}
                    onChange={(e) => {
                      const cityId = e.target.value;
                      setFormData((prev) => ({ ...prev, cityId, districtId: '' }));
                      setIlceler([]);
                    }}
                    required
                    disabled={illerLoading}
                  >
                    <option value="">İl Seçiniz</option>
                    {illerLoading ? (
                      <option disabled>İller yükleniyor...</option>
                    ) : iller.length === 0 ? (
                      <option disabled>İl bulunamadı</option>
                    ) : (
                      iller.map((il) => (
                        <option key={il.id || il._id} value={String(il.id || il._id)}>
                          {il.name || il.ad}
                        </option>
                      ))
                    )}
                  </select>
                </label>
                <label className="form-field">
                  <span>İlçe *</span>
                  <select
                    name="districtId"
                    value={formData.districtId}
                    onChange={(e) => setFormData((prev) => ({ ...prev, districtId: e.target.value }))}
                    required
                    disabled={!formData.cityId || ilcelerLoading}
                  >
                    <option value="">İlçe Seçiniz</option>
                    {ilcelerLoading ? (
                      <option disabled>İlçeler yükleniyor...</option>
                    ) : !formData.cityId || formData.cityId.trim() === '' ? (
                      <option disabled>Önce il seçiniz</option>
                    ) : ilceler.length === 0 ? (
                      <option disabled>İlçe bulunamadı</option>
                    ) : (
                      ilceler.map((ilce) => {
                        // API formatı: { id: number, adi: string, cityId: number }
                        const ilceId = ilce.id || ilce._id;
                        const ilceName = ilce.adi || ilce.ad || ilce.name || ilce.districtName || '';
                        return (
                          <option key={ilceId} value={String(ilceId)}>
                            {ilceName}
                          </option>
                        );
                      })
                    )}
                  </select>
                </label>
              </div>
              <div className="form-grid">
                <label className="form-field" style={{ gridColumn: '1 / -1' }}>
                  <span>Adres *</span>
                  <textarea 
                    name="adres" 
                    value={formData.adres} 
                    onChange={(e) => setFormData((prev) => ({ ...prev, adres: e.target.value }))} 
                    rows="3"
                    maxLength={500} 
                    required 
                  />
                </label>
              </div>
              <label className="form-field checkbox-field">
                <input
                  type="checkbox"
                  name="isTest"
                  checked={formData.isTest === true}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setFormData((prev) => ({ ...prev, isTest: checked }));
                  }}
                />
                <span>Test müşterisi</span>
              </label>
              {formError && <div className="error-state small">{formError}</div>}
            </form>
            <div 
              className="modal-footer"
              style={{
                flexShrink: 0,
                borderTop: '1px solid #e2e8f0',
                padding: '16px 24px',
                background: '#f8fafc'
              }}
            >
              <button type="button" className="secondary-btn" onClick={() => setIsAddModalOpen(false)}>
                İptal
              </button>
              <button 
                type="button" 
                className="primary-btn" 
                disabled={submitting}
                onClick={() => {
                  if (addCustomerFormRef.current) {
                    addCustomerFormRef.current.requestSubmit();
                  }
                }}
              >
                {submitting ? 'İlerleniyor...' : 'Devam'}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default CustomerManagementPage;



